import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, ChevronRight, ArrowRight, Coffee, Lock, ShieldCheck,
  BarChart3, Clock, TrendingUp, Receipt, Smartphone,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";

// Phase 25.16ax — DARA POS Live (public SMB tier).
//
// Trigger: Founder said 2026-06-02 "As for the POS Live, we should start
// at least $50 a month to get some traction first" + Burditt handed over
// a Dome Cafe lead (3 cafes, sophisticated POS) who Manuel needs to
// reach out to in learning-not-selling mode.
//
// POS Live is the ONLY public surface that keeps a visible price —
// because SMBs convert on transparent pricing (no procurement loop) and
// because $50/month is a self-serve impulse-purchase threshold. Every
// other DARA tier is "talk to us."

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POS_FEATURES = [
  {
    Icon: BarChart3,
    title: "Your Daily POS Brief",
    body: "Every morning by 7am: yesterday's sales vs the same day last week, your best and worst products, slow stock, your average ticket trend. Two minutes over coffee, in plain English. No spreadsheet.",
  },
  {
    Icon: TrendingUp,
    title: "Weekly Forward Look",
    body: "Sunday night, your week-ahead. Forecast revenue based on your trading pattern. Stock that's likely to run out. Staff hours you're under or over-rostered. Pre-empts your Monday morning.",
  },
  {
    Icon: Receipt,
    title: "Margin Watch",
    body: "Surfaces dishes/items where the margin has quietly dropped — supplier price creep, portion drift, comp drinks running hot. The stuff that bleeds 1-3% out of monthly margin and that nobody notices until the P&L comes in.",
  },
  {
    Icon: Clock,
    title: "Anomaly Alerts (within the hour)",
    body: "If something weird is happening today — a single station ringing 4x its normal hourly cover, a register taking too many voids, an unusual refund pattern — you get a text. Catch issues at lunch, not at quarter-end.",
  },
  {
    Icon: Smartphone,
    title: "Mobile-first",
    body: "Built for the phone in your pocket while you're behind the bar / on the floor / running between sites. Web app, no app-store install. Same login on desktop when you want a deeper view.",
  },
  {
    Icon: ShieldCheck,
    title: "Your data, your tenant",
    body: "Same architectural promise as DARA Enterprise: your POS data lives in your own workspace, AES-256 encrypted, every read audit-logged. We don't train on it. We don't sell it. Cancel and you get a full export.",
  },
];

const POS_VENDORS = [
  "Square", "Lightspeed", "Vend", "Kounta", "Impos", "Idealpos", "POSBOSS",
  "Bepoz", "Redcat", "Toast", "Loyverse", "Goodtill",
];

export default function PosLive() {
  usePageview();

  const [form, setForm] = useState({
    email: "", name: "", business_name: "", sites: "1",
    pos_vendor: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: form.business_name,
        role: `POS Live trial · ${form.sites} site(s)`,
        message: [
          form.pos_vendor ? `POS: ${form.pos_vendor}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "pos-live",
        source: "pos-live-page",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — we'll send your trial link within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="pos-live-page">
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="pos-back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 ml-2">
              · POS LIVE
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <a href="#trial" data-testid="pos-nav-trial-cta" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] sm:text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90">
              Start trial
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10283F]/40 via-transparent to-[#FFB000]/5 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 relative">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // POS LIVE · FOR HOSPITALITY + RETAIL SMBs · STARTING $50/MO
          </div>
          <div className="flex items-center gap-3 mb-4">
            <Coffee className="w-6 h-6 text-[#FFB000]" />
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-400">
              FOR CAFES · RESTAURANTS · BARS · RETAIL · MULTI-SITE OPERATORS
            </div>
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Your POS already has the numbers. <span className="text-[#FFB000]">We turn them into the brief that makes you money.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            POS Live plugs into your existing point-of-sale and sends you a daily brief in plain English — what sold, what didn't, what changed, what to do about it today. Built for owner-operators who don't have time to dig through reports, don't want a dashboard with 40 KPIs, and need a phone-sized answer to "is my business doing OK this morning?"
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            $50 a month per site. Two-week free trial. Cancel anytime, full data export. No setup fee, no annual contract, no procurement gauntlet.
          </p>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#trial"
              data-testid="pos-hero-trial-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <ChevronRight className="w-4 h-4" /> Start the 2-week trial <ArrowRight className="w-4 h-4" />
            </a>
            <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
              NO CARD · NO SETUP FEE · CANCEL ANYTIME
            </span>
          </div>
        </div>
      </section>

      {/* PRICING CARD · single visible price */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="border border-[#FFB000]/40 bg-black p-8 sm:p-10" data-testid="pos-pricing-card">
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-4">
              // POS LIVE · STARTER PRICING
            </div>
            <div className="flex items-baseline gap-3 mb-2">
              <span className="font-heading font-black text-5xl sm:text-6xl tracking-tight text-[#FFB000]">$50</span>
              <span className="font-mono text-sm uppercase tracking-widest text-zinc-400">per month · per site · AUD</span>
            </div>
            <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">
              One flat fee, billed monthly. Includes everything below. Multi-site operators get the second and third site at the same flat rate — no per-cover surcharge, no transaction-volume fee. If you outgrow Starter (you're running 10+ sites, you want multi-currency, you want the Hedge Ledger), talk to us about the bigger tier.
            </p>
            <a
              href="#trial"
              data-testid="pos-card-trial-cta"
              className="mt-6 inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              Start the 2-week trial <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // WHAT YOU GET · EVERY DAY
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            Six things, designed for owners who actually run the floor.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="pos-features-grid">
            {POS_FEATURES.map((f, i) => (
              <PosFeatureCard key={i} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* POS COMPATIBILITY */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // WHICH POS DOES DARA WORK WITH
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            If your POS exports data, <span className="text-[#FFB000]">DARA can read it.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            Direct connectors for the AU/NZ-popular POS vendors below. If yours isn't listed, we can almost always import via CSV / SFTP / email — we'll set it up with you during the free trial.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="pos-vendors-list">
            {POS_VENDORS.map((v) => (
              <span key={v} className="bg-black border border-zinc-800 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300">
                {v}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* DATA SAFETY · same Trust Strip as homepage but condensed */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // YOUR DATA · YOUR TENANT · YOUR KEYS
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            We don't move your data. <span className="text-[#FFB000]">We don't train on it. We log every read.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            Same architectural promise as DARA Enterprise. Your POS data is read into a workspace that's yours — per-business AES-256 encryption, full audit log on every interaction. We're not a data-mining company. We're the synthesis layer above the data your POS already produces.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <span className="flex items-center gap-2 bg-black border border-zinc-800 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300">
              <Lock className="w-3.5 h-3.5 text-[#FFB000]" /> Per-tenant AES-256
            </span>
            <span className="flex items-center gap-2 bg-black border border-zinc-800 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300">
              <ShieldCheck className="w-3.5 h-3.5 text-[#FFB000]" /> No training on your data
            </span>
            <span className="flex items-center gap-2 bg-black border border-zinc-800 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#FFB000]" /> Audit log every read
            </span>
            <span className="flex items-center gap-2 bg-black border border-zinc-800 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300">
              <ArrowRight className="w-3.5 h-3.5 text-[#FFB000]" /> Full export on cancel
            </span>
          </div>
        </div>
      </section>

      {/* TRIAL FORM */}
      <section id="trial" className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
                // START THE 2-WEEK TRIAL
              </div>
              <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight leading-tight">
                Free for 14 days. <span className="text-[#FFB000]">$50/month after that.</span>
              </h2>
              <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">
                We don't take your card up front. After 14 days, if you decide POS Live earns its keep, you switch on monthly billing. If not, your workspace exports cleanly and we're done. No call needed unless you want one — but if your POS is something unusual, we'll reach out to help you connect it.
              </p>
              <p className="mt-4 text-xs sm:text-[11px] text-zinc-500 leading-relaxed italic">
                Within one business day of submitting this form, you'll get a magic-link to your trial workspace. From there, connecting your POS takes about 5 minutes.
              </p>
            </div>

            {submitted ? (
              <div className="border border-[#FFB000]/40 bg-[#FFB000]/5 p-8" data-testid="pos-trial-success">
                <CheckCircle2 className="w-10 h-10 text-[#FFB000] mb-4" />
                <h3 className="font-heading font-bold text-xl tracking-tight mb-2">Trial requested.</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Watch your inbox — we'll send your trial workspace link within one business day. If you don't see it, check spam first, then ping <b>hello@daraos.ai</b>.
                </p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                  // POS LIVE · DARA OS
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="pos-trial-form">
                <Field label="Work email" required>
                  <input
                    type="email" required data-testid="pos-form-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                    placeholder="you@business.com.au"
                  />
                </Field>
                <Field label="Your name">
                  <input
                    type="text" data-testid="pos-form-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="Business name">
                  <input
                    type="text" data-testid="pos-form-business"
                    value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                    placeholder="The Dome Cafe Pty Ltd"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Number of sites">
                    <select
                      data-testid="pos-form-sites"
                      value={form.sites}
                      onChange={(e) => setForm({ ...form, sites: e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                    >
                      <option value="1">1 site</option>
                      <option value="2-3">2-3 sites</option>
                      <option value="4-10">4-10 sites</option>
                      <option value="10+">10+ sites</option>
                    </select>
                  </Field>
                  <Field label="Your POS">
                    <input
                      type="text" data-testid="pos-form-vendor"
                      value={form.pos_vendor}
                      onChange={(e) => setForm({ ...form, pos_vendor: e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                      placeholder="Square / Lightspeed / etc"
                    />
                  </Field>
                </div>
                <Field label="Anything else" sub="optional · what you're hoping POS Live solves">
                  <textarea
                    rows={3} data-testid="pos-form-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <button
                  type="submit" disabled={submitting} data-testid="pos-form-submit"
                  className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Start the 2-week trial →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="bg-black border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-xs sm:text-[11px] text-zinc-500 leading-relaxed font-mono">
            DARA OS · POS Live · majisonmanuel@gmail.com · +61 481 172 655 · Perth WA
          </p>
          <p className="mt-2 text-xs sm:text-[11px] text-zinc-600 leading-relaxed">
            Looking for the enterprise tier? <Link to="/pricing" className="text-[#FFB000] hover:underline">Talk to us</Link>.
          </p>
        </div>
      </footer>
    </div>
  );
}

function PosFeatureCard({ Icon, title, body }) {
  return (
    <div className="bg-black p-6 sm:p-8 flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-5 h-5 text-[#FFB000]" />
      </div>
      <h3 className="font-heading font-bold text-lg tracking-tight mb-3">{title}</h3>
      <p className="text-sm text-zinc-300 leading-relaxed">{body}</p>
    </div>
  );
}

function Field({ label, sub, required, children }) {
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5">
        {label}{required && <span className="text-[#FFB000] ml-1">*</span>}
        {sub && <span className="text-zinc-600 ml-2 normal-case tracking-normal italic">{sub}</span>}
      </label>
      {children}
    </div>
  );
}
